/**
 * Messaging bank for industry-specific ad landing pages.
 *
 * Each export follows the PageCopy shape and powers one Google Ads LP:
 *   /lp/ai-receptionist-hvac
 *   /lp/ai-receptionist-dental
 *   /lp/ai-receptionist-law
 *   /lp/ai-receptionist-plumbing
 *   /lp/ai-receptionist-real-estate
 *
 * Stats sourced from references/reference_missed-call-cost-2026.md (verified 2026-05-19).
 * CTA hrefs built via utmCta() from shared-ctas.ts.
 */

import { BRAND } from "./brand";
import { utmCta } from "./shared-ctas";
import type { PageCopy } from "./page-copy-types";

// ---------------------------------------------------------------------------
// HVAC
// ---------------------------------------------------------------------------
export const LP_HVAC_COPY: PageCopy = {
  eyebrow: "AI Receptionist for HVAC",
  h1: "Never Miss an HVAC Service Call Again",
  lede: `Emergency calls don't wait for business hours. BizRnR's AI receptionist answers every HVAC call 24/7 — dispatching techs, booking appointments, and capturing leads while you're on a job. Set up in ${BRAND.setupTimeClaim}. 30-day money-back guarantee.`,
  ctaPrimary: utmCta({
    label: "Get started",
    plan: "spark",
    source: "paid",
    medium: "cpc",
    campaign: "ai-receptionist-hvac",
  }),
  ctaSecondary: { label: "See how it works", href: "#how-it-works" },
  trustSignals: [
    "$99/mo AI Receptionist",
    "30-day money-back guarantee",
    "Cancel anytime",
  ],
  sections: [
    { heading: "The cost of missed HVAC calls" },
    {
      heading: "How it works",
      body: "Forward your number and your AI receptionist starts answering HVAC calls in 60 seconds.",
    },
    { heading: "Built for HVAC businesses" },
    { heading: "Compare" },
    { heading: "Pricing" },
  ],
  faqs: [
    {
      q: "How does BizRnR handle emergency HVAC calls after hours?",
      a: "Your AI receptionist answers every call 24/7, including weekends and holidays. It identifies emergency situations (no heat in winter, AC failures in summer, gas smell reports) and routes them to your on-call technician immediately via text and call. Non-urgent requests are scheduled for the next available slot.",
    },
    {
      q: "Can the AI book HVAC service appointments?",
      a: "Yes. Your AI receptionist checks your calendar availability and books service appointments in real time during the call. It collects the service address, describes the issue, and confirms the appointment — no double-booking, no phone tag.",
    },
    {
      q: "Does it integrate with ServiceTitan or Housecall Pro?",
      a: "BizRnR works alongside your existing field service software. Forward your business number to BizRnR, and your AI receptionist handles the call. Appointment details, caller info, and call transcripts are available in your BizRnR dashboard for easy reference when updating ServiceTitan, Housecall Pro, or any other system.",
    },
    {
      q: "What happens during seasonal demand spikes?",
      a: "Unlike a human receptionist who can only handle one call at a time, your AI receptionist handles unlimited concurrent calls. During the first heat wave or cold snap of the season, every call gets answered on the first ring — no busy signals, no voicemail, no lost jobs.",
    },
    {
      q: "How does the AI qualify HVAC leads?",
      a: "Your AI receptionist asks callers about the type of service needed (repair, maintenance, installation), the equipment type, the urgency level, and whether they are an existing customer. This information is included in your instant text notification so you can prioritize and dispatch efficiently.",
    },
    {
      q: "What is included in the AI Receptionist plan?",
      a: "Everything you need: AI receptionist answering every call 24/7, appointment booking, 100 AI interactions per month, a dedicated local phone number, missed-call SMS text-back, call transcripts, and recordings. $99/month with cancel-anytime billing and a 30-day money-back guarantee.",
    },
    {
      q: "Can I keep my existing HVAC business number?",
      a: "Yes. You keep your existing number and set up call forwarding to your BizRnR number. Customers dial your current number and your AI receptionist answers. You also get a dedicated local number you can use on trucks, yard signs, and ads.",
    },
    {
      q: "What if I want to cancel?",
      a: "Cancel anytime with no long-term contract. If BizRnR is not the right fit, cancel within 30 days and we will refund the subscription charge.",
    },
  ],
  metadata: {
    title: "Never Miss an HVAC Service Call — AI Receptionist | BizRnR",
    description:
      "62% of HVAC calls go unanswered. BizRnR AI receptionist answers every call 24/7, books service appointments, and dispatches techs. $99/mo AI Receptionist plan. 30-day money-back guarantee.",
    canonical: "https://bizrnr.com/lp/ai-receptionist-hvac",
  },
};

// ---------------------------------------------------------------------------
// Dental
// ---------------------------------------------------------------------------
export const LP_DENTAL_COPY: PageCopy = {
  eyebrow: "AI Receptionist for Dental Practices",
  h1: "Never Miss a Patient Call Again",
  lede: `New patients call once — if no one answers, they book with another practice. BizRnR's AI receptionist answers every dental call 24/7, schedules appointments, and handles intake questions. Set up in ${BRAND.setupTimeClaim}. 30-day money-back guarantee.`,
  ctaPrimary: utmCta({
    label: "Get started",
    plan: "spark",
    source: "paid",
    medium: "cpc",
    campaign: "ai-receptionist-dental",
  }),
  ctaSecondary: { label: "See how it works", href: "#how-it-works" },
  trustSignals: [
    "$99/mo AI Receptionist",
    "30-day money-back guarantee",
    "Cancel anytime",
  ],
  sections: [
    { heading: "The cost of missed patient calls" },
    {
      heading: "How it works",
      body: "Forward your practice number and your AI receptionist starts answering in 60 seconds.",
    },
    { heading: "Built for dental practices" },
    { heading: "Compare" },
    { heading: "Pricing" },
  ],
  faqs: [
    {
      q: "How does BizRnR handle new patient inquiries after hours?",
      a: "Your AI receptionist answers every call 24/7 with your practice greeting. It collects new patient information, answers common questions about services and insurance acceptance, and books the first available appointment. You get an instant text summary with the patient details.",
    },
    {
      q: "Can the AI schedule dental appointments?",
      a: "Yes. Your AI receptionist checks your calendar availability and books appointments in real time during the call. It handles new patient scheduling, recall appointments, and urgent care requests — no double-booking, no phone tag.",
    },
    {
      q: "How does it help reduce no-shows?",
      a: "When patients call to reschedule instead of no-showing, your AI receptionist handles the change instantly. It can also confirm upcoming appointments and reschedule cancellations to fill gaps in your schedule. Fewer empty chairs, more production per day.",
    },
    {
      q: "Does it handle insurance verification questions?",
      a: "Your AI receptionist can answer common insurance questions based on the information you provide during setup — which plans you accept, whether you offer payment plans, and general coverage questions. For specific verification, it captures the insurance details and routes them to your front desk team.",
    },
    {
      q: "Is BizRnR HIPAA-conscious?",
      a: "Yes. BizRnR uses HIPAA-conscious data handling practices. Call recordings and transcripts are stored securely, and patient information is handled with appropriate privacy safeguards. Note: BizRnR is designed as a receptionist and scheduling tool, not a clinical system.",
    },
    {
      q: "Can it support multiple practice locations?",
      a: "Yes. Each location can have its own forwarding number, greeting, hours, and calendar. Your AI receptionist routes calls appropriately based on the number dialed, so each location gets a personalized experience.",
    },
    {
      q: "What is included in the AI Receptionist plan?",
      a: "Everything your practice needs: AI receptionist answering every call 24/7, appointment booking, 100 AI interactions per month, a dedicated local phone number, missed-call SMS text-back, call transcripts, and recordings. $99/month with cancel-anytime billing and a 30-day money-back guarantee.",
    },
    {
      q: "What if I want to cancel?",
      a: "Cancel anytime with no long-term contract. If BizRnR is not the right fit, cancel within 30 days and we will refund the subscription charge.",
    },
  ],
  metadata: {
    title: "Never Miss a Patient Call — AI Receptionist for Dentists | BizRnR",
    description:
      "62% of patient calls go unanswered. BizRnR AI receptionist answers every call 24/7, books appointments, and handles patient intake. $99/mo. 30-day money-back guarantee.",
    canonical: "https://bizrnr.com/lp/ai-receptionist-dental",
  },
};

// ---------------------------------------------------------------------------
// Law
// ---------------------------------------------------------------------------
export const LP_LAW_COPY: PageCopy = {
  eyebrow: "AI Receptionist for Law Firms",
  h1: "Your Law Firm AI receptionist answers every call, 24/7/365 so you never lose a client.",
  lede: `Legal callers are shopping multiple firms — the first to answer wins the case. BizRnR's AI receptionist answers every call 24/7, qualifies intake, and books consultations. Set up in ${BRAND.setupTimeClaim}. 30-day money-back guarantee.`,
  ctaPrimary: utmCta({
    label: "Get started",
    plan: "spark",
    source: "paid",
    medium: "cpc",
    campaign: "ai-receptionist-law",
  }),
  ctaSecondary: { label: "See how it works", href: "#how-it-works" },
  trustSignals: [
    "$99/mo AI Receptionist",
    "30-day money-back guarantee",
    "Cancel anytime",
  ],
  sections: [
    { heading: "The cost of missed intake calls" },
    {
      heading: "How it works",
      body: "Forward your firm number and your AI receptionist starts answering in 60 seconds.",
    },
    { heading: "Built for law firms" },
    { heading: "Compare" },
    { heading: "Pricing" },
  ],
  faqs: [
    {
      q: "How does BizRnR handle after-hours legal intake calls?",
      a: "Your AI receptionist answers every call 24/7. It identifies the case type, collects essential intake information (name, contact, brief description of the legal matter), and books a consultation on your calendar. Urgent matters are flagged and routed to your on-call attorney via text notification.",
    },
    {
      q: "Can the AI qualify legal intake leads?",
      a: "Yes. Your AI receptionist asks callers about their legal matter, timeline, and whether they have representation. It routes cases by type (personal injury, family law, criminal defense, etc.) based on your practice areas. You get a structured intake summary with every call.",
    },
    {
      q: "Does it help with conflict checking?",
      a: "Your AI receptionist collects opposing party information during intake when configured. This gives your team the data needed to run conflict checks before the first consultation, saving attorney time and avoiding ethical issues.",
    },
    {
      q: "Can it route calls by case type or attorney?",
      a: "Yes. Configure your AI receptionist to route personal injury calls to one attorney, family law to another, and criminal defense to a third. During business hours, it can transfer directly. After hours, it books the consultation with the appropriate attorney and sends them a text summary.",
    },
    {
      q: "Does BizRnR integrate with legal CRMs like Clio or MyCase?",
      a: "BizRnR works alongside your existing legal software. Intake details, caller information, and call transcripts are available in your BizRnR dashboard for easy reference when updating Clio, MyCase, PracticePanther, or any other system.",
    },
    {
      q: "How does the AI handle urgent after-hours consultations?",
      a: "You define what constitutes urgent for your firm — arrests, restraining orders, time-sensitive filings. Your AI receptionist screens for these triggers and immediately notifies the on-call attorney via text with the caller details and urgency level.",
    },
    {
      q: "What is included in the AI Receptionist plan?",
      a: "Everything your firm needs: AI receptionist answering every call 24/7, intake qualification, consultation booking, 100 AI interactions per month, a dedicated local phone number, missed-call SMS text-back, call transcripts, and recordings. $99/month with cancel-anytime billing and a 30-day money-back guarantee.",
    },
    {
      q: "What if I want to cancel?",
      a: "Cancel anytime with no long-term contract. If BizRnR is not the right fit, cancel within 30 days and we will refund the subscription charge.",
    },
  ],
  metadata: {
    title:
      "Never Miss a Client Intake Call — AI Receptionist for Law Firms | BizRnR",
    description:
      "62% of legal intake calls go unanswered. BizRnR AI receptionist answers every call 24/7, qualifies intake, and books consultations. $99/mo. 30-day money-back guarantee.",
    canonical: "https://bizrnr.com/lp/ai-receptionist-law",
  },
};

// ---------------------------------------------------------------------------
// Plumbing
// ---------------------------------------------------------------------------
export const LP_PLUMBING_COPY: PageCopy = {
  eyebrow: "AI Receptionist for Plumbers",
  h1: "Never Miss an Emergency Plumbing Call Again",
  lede: `Burst pipes don't wait for business hours. BizRnR's AI receptionist answers every plumbing call 24/7 — routing emergencies, booking jobs, and capturing leads while you're under a sink. Set up in ${BRAND.setupTimeClaim}. 30-day money-back guarantee.`,
  ctaPrimary: utmCta({
    label: "Get started",
    plan: "spark",
    source: "paid",
    medium: "cpc",
    campaign: "ai-receptionist-plumbing",
  }),
  ctaSecondary: { label: "See how it works", href: "#how-it-works" },
  trustSignals: [
    "$99/mo AI Receptionist",
    "30-day money-back guarantee",
    "Cancel anytime",
  ],
  sections: [
    { heading: "The cost of missed plumbing calls" },
    {
      heading: "How it works",
      body: "Forward your number and your AI receptionist starts answering plumbing calls in 60 seconds.",
    },
    { heading: "Built for plumbing businesses" },
    { heading: "Compare" },
    { heading: "Pricing" },
  ],
  faqs: [
    {
      q: "How does BizRnR handle emergency plumbing calls?",
      a: "Your AI receptionist answers every call 24/7, including weekends and holidays. It identifies emergencies (burst pipes, sewage backups, gas leaks, flooding) and immediately routes them to your on-call plumber via text and call. Non-urgent requests are scheduled for the next available slot.",
    },
    {
      q: "Can the AI book plumbing service appointments?",
      a: "Yes. Your AI receptionist checks your calendar availability and books service appointments in real time during the call. It collects the service address, describes the issue, and confirms the appointment. No double-booking, no phone tag.",
    },
    {
      q: "Does it work with Jobber?",
      a: "BizRnR works alongside your existing field service software. Forward your business number to BizRnR, and your AI receptionist handles the call. Appointment details, caller info, and call transcripts are available in your BizRnR dashboard for easy reference when updating Jobber, Housecall Pro, ServiceTitan, or any other system.",
    },
    {
      q: "What happens when I am on a job and cannot answer?",
      a: "That is exactly when BizRnR shines. Your AI receptionist answers the call, qualifies the lead, books the appointment, and sends you a text summary — all while you are focused on the job in front of you. No lost leads, no interruptions.",
    },
    {
      q: "Can the AI handle estimate requests?",
      a: "Your AI receptionist collects the details needed for an estimate — type of work, scope, location, and urgency. It sends you a summary so you can prepare an accurate estimate and call the customer back with a quote, or it books an on-site estimate appointment directly on your calendar.",
    },
    {
      q: "How does it handle seasonal call spikes?",
      a: "Frozen pipes in winter, sewer line backups in spring — your AI receptionist handles unlimited concurrent calls. Every call gets answered on the first ring during the busiest days. No busy signals, no voicemail, no lost jobs.",
    },
    {
      q: "What is included in the AI Receptionist plan?",
      a: "Everything your plumbing business needs: AI receptionist answering every call 24/7, job booking, 100 AI interactions per month, a dedicated local phone number, missed-call SMS text-back, call transcripts, and recordings. $99/month with cancel-anytime billing and a 30-day money-back guarantee.",
    },
    {
      q: "What if I want to cancel?",
      a: "Cancel anytime with no long-term contract. If BizRnR is not the right fit, cancel within 30 days and we will refund the subscription charge.",
    },
  ],
  metadata: {
    title: "Never Miss an Emergency Plumbing Call — AI Receptionist | BizRnR",
    description:
      "62% of plumbing calls go unanswered. BizRnR AI receptionist answers every call 24/7, routes emergencies, and books jobs. $99/mo AI Receptionist plan. 30-day money-back guarantee.",
    canonical: "https://bizrnr.com/lp/ai-receptionist-plumbing",
  },
};

// ---------------------------------------------------------------------------
// Real Estate
// ---------------------------------------------------------------------------
export const LP_REAL_ESTATE_FIT_COPY = {
  liveHumanRequired: "You require a live human receptionist for every caller.",
} as const;

export const LP_REAL_ESTATE_COPY: PageCopy = {
  eyebrow: "AI Receptionist for Real Estate Agents",
  h1: "AI receptionist for real estate agents with configurable buyer and seller intake.",
  lede: `BizRnR provides configurable 24/7 inbound call handling, buyer and seller intake, supported calendar booking, and structured call records. Agents remain responsible for property facts, representation, disclosures, and professional judgment.`,
  ctaPrimary: utmCta({
    label: "Get started",
    plan: "spark",
    source: "paid",
    medium: "cpc",
    campaign: "ai-receptionist-real-estate",
  }),
  ctaSecondary: { label: "See how it works", href: "#how-it-works" },
  trustSignals: [
    "$99/mo AI Receptionist",
    "30-day money-back guarantee",
    "Cancel anytime",
  ],
  sections: [
    { heading: "Why consistent real estate call intake matters" },
    {
      heading: "How it works",
      body: "Configure buyer and seller questions, connect phone and supported calendar workflows, test, and activate.",
    },
    { heading: "Built for real estate call workflows" },
    { heading: "Compare" },
    { heading: "Pricing" },
  ],
  faqs: [
    {
      q: "Can the AI schedule listing consultations and showing requests?",
      a: "BizRnR can offer approved availability through a supported connected calendar and record the property, caller details, and requested appointment. The brokerage controls availability and final confirmation rules.",
    },
    {
      q: "Can it collect buyer and seller information?",
      a: "Yes. The workflow can ask approved questions about buyer or seller intent, property interest, budget range, timing, financing status, address, representation status, and requested next steps.",
    },
    {
      q: "Can it capture calls from yard signs and listing ads?",
      a: "Yes. A dedicated or forwarded number can capture the property the caller names and preserve their question for the agent. BizRnR should use approved information or request human follow-up rather than invent live property facts.",
    },
    {
      q: "What happens when the agent is unavailable?",
      a: "BizRnR can follow the configured greeting, intake, scheduling, routing, and fallback rules, then create a structured call record for the responsible agent.",
    },
    {
      q: "Does BizRnR have live MLS access?",
      a: "BizRnR does not claim independent live MLS access. Agents remain responsible for price, availability, property condition, access, disclosures, representation, and fair-housing compliance.",
    },
    {
      q: "Does BizRnR require a real estate CRM?",
      a: "No. BizRnR can create a structured call record without a real estate CRM. Verify any required native Follow Up Boss, kvCORE, BoomTown, or other connector before purchasing.",
    },
    {
      q: "What is included in the starting plan?",
      a: "BizRnR publishes a $99 monthly starting price. Current included usage, overage pricing, guarantees, and plan terms should be verified on the pricing page.",
    },
    {
      q: "What if I want to cancel?",
      a: "Cancel anytime with no long-term contract. If BizRnR is not the right fit, cancel within 30 days and we will refund the subscription charge.",
    },
  ],
  metadata: {
    title: "AI Receptionist for Real Estate Agents | BizRnR",
    description:
      "BizRnR answers real estate calls 24/7, captures buyer and seller details, and supports showing or consultation booking from $99 per month.",
    canonical: "https://bizrnr.com/lp/ai-receptionist-real-estate",
  },
};
